import React from 'react';
import { Link } from 'react-router';
import { getTimeLeftLabel } from 'common/utils';

const INTRO_STEP = ({ project, wrapup, isCreative }) => {
  let description;
  let title = "Wrap-up Tour";

  if (wrapup) {
    title = "Welcome to Wrap-up.";
    description = <span>
      Congratulations on awarding your project.<br/>
      What happens next?
    </span>;

    if (isCreative) {
      description = <span>
        Congratulations, you { wrapup.award.type === "offer"? "received an offer" : "won"}!<br/>
        Now what?
      </span>;
    }
  } else if (project.status === 'closed') {
    description = <span>
      Your project has closed. You have up to 7 days
      to <Link target="_blank" to="/help/managing-project/how-do-i-award-entry-my-project/">award</Link> your
      favorite entry.<br/>
      What happens after you award?
    </span>;
  } else if (project.status === 'open') {
    const timeLeft = getTimeLeftLabel(project.deadline, {
      negativeLabel: "0 days",
      daysUnit: " days",
      hoursUnit: " hours",
      minutesUnit: " minutes"
    });

    description = <span>
      Your project will end in { timeLeft } and then you will have up to 7 days
      to <Link target="_blank" to="/help/managing-project/how-do-i-award-entry-my-project/">award</Link> your
      favorite entry.<br/>
      What happens after you award?
    </span>;
  }

  return {
    show: true,
    intro: true,
    title,
    description
  };
}

const MULTIPLE_AWARDS_STEP = ({ awards, isCreative }) => ({
  show: awards.length > 1 && !isCreative,
  title: "You have multiple awards.",
  image: "https://s3.amazonaws.com/crowdspring3-assets/marketing/crowdspring-wrapup-tour-client-multiple-awards.png",
  description: <span>
    Each award has a separate wrap-up. <br className="hide-for-small-only"/>
    You can access each in the wrap-up menu.
  </span>
});

const AWARD_STEP = ({ isCreative, project }) => ({
  show: !isCreative,
  header: "Before you begin wrap-up",
  title: "Award and sign the contract.",
  image: project.entry_type === "image"? "https://s3.amazonaws.com/crowdspring3-assets/marketing/crowdspring-wrapup-tour-client-sign-contract.png" : "https://s3.amazonaws.com/crowdspring3-assets/marketing/crowdspring-wrapup-tour-client-naming-sign-contract.png",
  description: <span>
    Award your favorite entry and sign the contract. <br className="hide-for-small-only"/>
    The contract transfers full intellectual property rights to you.
  </span>
});

const APPROVE_PROOFS_STEP = ({ isCreative, project }) => ({
  show: project.entry_type === "image" && !project.is_one_to_one,
  header: "Step 1",
  title: isCreative? "Sign contract and send proofs." : "Approve proofs.",
  image: isCreative? "https://s3.amazonaws.com/crowdspring3-assets/marketing/crowdspring-wrapup-tour-creative-upload-proofs.png" : "https://s3.amazonaws.com/crowdspring3-assets/marketing/crowdspring-wrapup-tour-client-approve-proofs.png",
  description: isCreative? <span>
    The contract protects both you and the client. You'll send a proof of your
    work using a proofsheet template. The client may offer feedback. You'll
    collaborate to make any final changes to your design.
  </span> : <span>
    The creative will send you proofs of their work. You can still request
    changes and tweaks during this step. When you're happy with the proofs,
    approve them to get final files.
  </span>
});

const APPROVE_FINAL_FILES_STEP = ({ project, isCreative }) => {
  let title = "Approve final files.";
  let description = <span>
    The creative will send you the final files of their work. Once you have everything
    you need, approve the final files. Remember, the creative will not be paid until
    you approve the final files.
  </span>;

  if (isCreative) {
    title = "Send final files.";
    description = <span>
      After the client approves your proofs, you will send them the final files for you work.
      The client may ask for small changes. Once they approve your final files, you will be
      able to request a payout of the award.
    </span>;

    if (project.is_one_to_one) {
      title = "Sign contract & send final files.";
      description = <span>
        The contract protects both you and the client. You will send them the final files for
        you work. The client may ask for small changes. Once they approve your final files, you will
        be able to request a payout of the award.
      </span>;
    }
  }

  return ({
    show: project.entry_type === "image",
    header: `Step ${project.is_one_to_one? '1': '2'}`,
    image: isCreative? "https://s3.amazonaws.com/crowdspring3-assets/marketing/crowdspring-wrapup-tour-creative-add-final-files.png" : "https://s3.amazonaws.com/crowdspring3-assets/marketing/crowdspring-wrapup-tour-client-approve-final-files.png",
    title,
    description
  })
};

const APPROVE_NAME_STEP = ({ isCreative, project }) => ({
  show: project.entry_type === "name",
  header: "Step 1",
  title: isCreative? "Sign the contract." : "Approve the name.",
  image: isCreative? "https://s3.amazonaws.com/crowdspring3-assets/marketing/crowdspring-wrapup-tour-creative-design-sign-contract.png" : "https://s3.amazonaws.com/crowdspring3-assets/marketing/crowdspring-wrapup-tour-client-approve-name.png",
  description: isCreative? <span>
    Sign the contract. The contract transfers full intellectual property rights to the client.
  </span> : <span>
    Approve the name you picked to complete the project. Remember, the creative will not
  be paid (and you will not officially own the name) until you approve the name.
  </span>
});

const LEAVE_FEEDBACK_STEP = ({ project, isCreative }) => ({
  show: true,
  header: `Step ${project.is_one_to_one || project.entry_type === 'name'? '2': '3'}`,
  title: "Leave feedback.",
  image: isCreative? "https://s3.amazonaws.com/crowdspring3-assets/marketing/crowdspring-wrapup-tour-creative-feedback.png" : "https://s3.amazonaws.com/crowdspring3-assets/marketing/crowdspring-wrapup-tour-client-feedback.png",
  description: isCreative? <span>
    You and the client will be able to rate each other and provide feedback.
  </span> : <span>
    Your feedback and ratings are crucial to the creative's reputation on crowdspring.
    And don't forget to tell us how you liked crowdspring - we value your feedback
    and use it to improve our product and service.
  </span>
});

const FINAL_STEP = ({ project, isCreative }) => {
  const guideUrl = (
    `/help/guides/${project.entry_type === 'name'? 'naming': 'design'}-projects-wrapup/`
  );
  const shorter = project.entry_type === 'name' && !project.is_one_to_one;
  const duration = shorter? 7 : 14;
  const avgDuration = shorter? 2 : 7;

  return {
    show: true,
    title: "That's it!",
    image: "https://s3.amazonaws.com/crowdspring3-assets/marketing/crowdspring_marketing_support-2x.png",
    description: <span>
      You have { duration } days to complete wrap-up from the time you award your project, but
      most { isCreative? "wrap-ups" : "clients" } finish
      in { avgDuration } days. If you have any questions, please consult
      the <Link target="_blank" to={guideUrl}>wrap-up guide</Link>, or contact
      our <Link target="_blank" to="/help/contact/">customer support</Link> team.
    </span>
  };
};

/* steps above in the order they should appear */
const ALL_STEPS = [
  INTRO_STEP, MULTIPLE_AWARDS_STEP, AWARD_STEP, APPROVE_PROOFS_STEP, APPROVE_FINAL_FILES_STEP,
  APPROVE_NAME_STEP, LEAVE_FEEDBACK_STEP, FINAL_STEP
];

export const getSteps = ({ user, project, wrapup, awards }) => {
  const isCreative = wrapup && user.id === wrapup.creative.id;

  return ALL_STEPS.reduce((steps, stepFunc) => {
    const step = stepFunc({ project, wrapup, awards, isCreative });
    if (step.show) {
      return [...steps, step];
    }
    return steps;
  }, []);
}



// WEBPACK FOOTER //
// ./project/components/wrapup/all_tour_steps.jsx